import { useNavigate } from "react-router-dom";

import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import { SaudiRiyal } from 'lucide-react';
import { Box, Tab, Tabs } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Line,
} from "recharts";
import VehicleListings from "../../features/manageVehicles/vehicleListings";
import React, { useState } from "react";
import SearchBar from "../../components/SearchBar";
import { useGetDashboardAnalyticsQuery, useGetDashboardGraphQuery, useGetRevenueGraphQuery } from "../../service/features/api";
import { VEHICLE_TYPE } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const tabTypes = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
].map(type => getVehicleTypeLabel(type));



const Dashboard = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const { data: dashboardData } = useGetDashboardAnalyticsQuery();
  const stats = dashboardData?.data;

  const [period, setPeriod] = useState("monthly");
  const { data: graphDataResponse } = useGetDashboardGraphQuery({ period });
  const graphData = graphDataResponse?.data || [];
  const [revenuePeriod, setRevenuePeriod] = useState("monthly");

  const { data: revenueGraphResponse } = useGetRevenueGraphQuery({ period: revenuePeriod });
  const revenueGraphData = revenueGraphResponse?.data || [];

  // console.log("Revenue Graph Data:", revenueGraphData);

  return (
    <>
      <div className="main_title">
        <h1>لوحة المعلومات / Dashboard</h1>
        <p>مرحبًا بعودتك! إليك ما يحدث في سوق السيارات الخاص بك / Welcome back! Here's what's happening with your car marketplace.</p>
      </div>

      {/* STATS */}
      <div className="stats_boxes gap_m mb_20">
        <div className="cards" onClick={() => navigate("/manage-users")}>
          <figure><PeopleAltIcon /></figure>
          <h2>{stats?.users?.toLocaleString() || 0}</h2>
          <p>إجمالي العملاء / Total Customers</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-dealers")}>
          <figure><WarehouseIcon /></figure>
          <h2>{stats?.dealers?.toLocaleString() || 0}</h2>
          <p>إجمالي الوكلاء/البائعين / Total Dealers/Sellers</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-vehicles")}>
          <figure><DirectionsCarFilledIcon /></figure>
          <h2>{stats?.cars?.toLocaleString() || 0}</h2>
          <p>إجمالي المركبات المدرجة / Total Listed Vehicles</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-revenue")}>
          <figure><SaudiRiyal /></figure>
          <h2 style={{ display: 'flex', gap: '5px' }}><SaudiRiyal /> {stats?.totalRevenue?.toLocaleString() || 0}</h2>
          <p>إجمالي الإيرادات / Total Revenue</p>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid_sc gap_m mb_20">
        {/* TOTAL USERS */}
        <div className="cards table_card">
          <div className="cards_header mb_20">
            <div className="left_s">
              <h2>إجمالي المستخدمين / Total Users</h2>
            </div>
            <div className="right_s">
              <p>
                <span 
                  className={period === "daily" ? "active" : ""} 
                  onClick={() => setPeriod("daily")}
                  style={{ cursor: 'pointer' }}
                >A day / يوم</span>
                <span 
                  className={period === "weekly" ? "active" : ""} 
                  onClick={() => setPeriod("weekly")}
                  style={{ cursor: 'pointer' }}
                >A week / أسبوع</span>
                <span 
                  className={period === "monthly" ? "active" : ""} 
                  onClick={() => setPeriod("monthly")}
                  style={{ cursor: 'pointer' }}
                >A month / شهر</span>
                <span 
                  className={period === "yearly" ? "active" : ""} 
                  onClick={() => setPeriod("yearly")}
                  style={{ cursor: 'pointer' }}
                >A year / سنة</span>
              </p>
              
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={graphData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#38B6FF" name="Users" />
              <Bar dataKey="dealers" fill="#FF9F40" name="Dealers/Sellers" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* TOTAL REVENUE */}
        <div className="cards table_card">
          <div className="cards_header mb_20">
            <div className="left_s">
              <h2>إجمالي الإيرادات / Total Revenue</h2>
            </div>
            <div className="right_s">
              <p>
                <span 
                  className={revenuePeriod === "daily" ? "active" : ""} 
                  onClick={() => setRevenuePeriod("daily")}
                  style={{ cursor: 'pointer' }}
                >A day / يوم</span>
                <span 
                  className={revenuePeriod === "weekly" ? "active" : ""} 
                  onClick={() => setRevenuePeriod("weekly")}
                  style={{ cursor: 'pointer' }}
                >A week / أسبوع</span>
                <span 
                  className={revenuePeriod === "monthly" ? "active" : ""} 
                  onClick={() => setRevenuePeriod("monthly")}
                  style={{ cursor: 'pointer' }}
                >A month / شهر</span>
                <span 
                  className={revenuePeriod === "yearly" ? "active" : ""} 
                  onClick={() => setRevenuePeriod("yearly")}
                  style={{ cursor: 'pointer' }}
                >A year / سنة</span>
              </p>
              
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={revenueGraphData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#38B6FF"
                strokeWidth={3}
                name="Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="cards mb_20">
        <div className="cards_header mb_20">
          <h2>الإعلانات المميزة / Featured Listings</h2>
        </div>

        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="بحث عن المركبات / Search Vehicles..."
            />
          </div>
          <div className="right_s">
            <Box className="custom_tabs" sx={{ width: "100%" }}>
              <Tabs
                value={valueTabs}
                onChange={handleChangeTabs}
                className="custom_tabs_links mb_0"
              >
                {tabTypes.map((label, index) => (
                  <Tab key={label} label={label} {...a11yProps(index)} />
                ))}
              </Tabs>
            </Box>
          </div>
        </div>
        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            <VehicleListings type={type} searchTerm={searchTerm} featured={true} module="Dashboard" />
          </CustomTabPanel>
        ))}
      </div>

    </>
  );
};

export default Dashboard;